import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import {
  RS_BOARD_FORM_FOCUS_ATTR,
  RS_NARROW_BOARD_MQ,
  isRsBoardFormFocused,
  isRsNarrowBoardLayout,
  setRsBoardFormFocus,
} from '../utils/reservationBoardCalendar.util';

const SHARE_BAR_SELECTOR = '.layout-reservation-share-bar';
const TOOLBAR_SELECTOR = '[data-rs-calendar-toolbar]';
const HEADER_SELECTOR = '[data-rs-calendar-header]';
const LAYOUT_SELECTOR = '[data-rs-board-layout]';
const SIDEBAR_SELECTOR = '[data-rs-sidebar]';

/** 予約表の狭い画面（サイドバー縦積み／固定登録バー）閾値の購読 */
export function useRsNarrowBoardLayout(): boolean {
  const [narrow, setNarrow] = useState(() => isRsNarrowBoardLayout());

  useEffect(() => {
    const mq = window.matchMedia(RS_NARROW_BOARD_MQ);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return narrow;
}

/**
 * 共有バー＋カレンダー sticky 帯の実高さを CSS 変数へ同期する。
 * --rs-share-bar-offset: 共有バー高さ（帯の top）
 * --rs-cal-toolbar-offset: 共有バー＋帯（列ヘッダ top / scroll-margin）
 * --rs-cal-header-offset: 項目ヘッダ行の高さ（日セル scroll-margin 加算）
 *
 * 狭い画面: 名前未入力かつカレンダー列が視野外まで送れたら form-focus
 * （入力済みはカレンダーを残し、Round 116 のマス選択方針と揃える）。
 */
export function useRsCalendarStickyOffsets(
  columnRef: RefObject<HTMLElement | null>,
  syncKey: string | number | boolean | null | undefined,
  options?: { enableScrollFormFocus?: boolean }
): void {
  const enableScrollFormFocus = options?.enableScrollFormFocus ?? true;

  useLayoutEffect(() => {
    const column = columnRef.current;
    const toolbar = column?.querySelector<HTMLElement>(TOOLBAR_SELECTOR);
    if (!column || !toolbar) return;

    const root =
      column.closest<HTMLElement>(LAYOUT_SELECTOR) ?? column.parentElement ?? column;
    const sidebar = root.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
    const narrowMq = window.matchMedia(RS_NARROW_BOARD_MQ);

    const syncOffsets = () => {
      const shareBar = document.querySelector<HTMLElement>(SHARE_BAR_SELECTOR);
      const header = column.querySelector<HTMLElement>(HEADER_SELECTOR);
      const shareH = shareBar ? Math.ceil(shareBar.getBoundingClientRect().height) : 0;
      const chromeH = Math.ceil(toolbar.getBoundingClientRect().height);
      const headerH = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
      root.style.setProperty('--rs-share-bar-offset', `${shareH}px`);
      // form-focus でカレンダーが畳まれ高さ0のとき、既存の余白変数を壊さない
      if (chromeH > 0) {
        root.style.setProperty('--rs-cal-toolbar-offset', `${shareH + chromeH}px`);
      } else if (shareH > 0) {
        const prev =
          Number.parseFloat(getComputedStyle(root).getPropertyValue('--rs-cal-toolbar-offset')) || 0;
        if (prev < shareH) {
          root.style.setProperty('--rs-cal-toolbar-offset', `${shareH}px`);
        }
      }
      if (headerH > 0) {
        root.style.setProperty('--rs-cal-header-offset', `${headerH}px`);
      }
    };

    const syncFormFocus = () => {
      if (!narrowMq.matches || !enableScrollFormFocus) {
        if (!narrowMq.matches) setRsBoardFormFocus(false, root);
        return;
      }
      // jump 等で既に立っているあいだは維持（畳み込み中も column は残る）
      if (isRsBoardFormFocused(root)) return;

      const shareH =
        Number.parseFloat(getComputedStyle(root).getPropertyValue('--rs-share-bar-offset')) || 0;
      const colRect = column.getBoundingClientRect();
      // 初回レイアウト中（高さ未確定）やページ先頭では誤発火させない
      if (colRect.height < 160 || window.scrollY < 48) return;
      // カレンダー下端が共有バーより上＝完全に送り出したときだけ
      if (colRect.bottom <= shareH + 4) {
        setRsBoardFormFocus(true, root);
      }
    };

    const sync = () => {
      syncOffsets();
      syncFormFocus();
    };

    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(column);
    ro.observe(toolbar);
    if (sidebar) ro.observe(sidebar);
    const shareBar = document.querySelector(SHARE_BAR_SELECTOR);
    if (shareBar) ro.observe(shareBar);

    window.addEventListener('scroll', syncFormFocus, { passive: true });
    narrowMq.addEventListener('change', sync);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', syncFormFocus);
      narrowMq.removeEventListener('change', sync);
      root.removeAttribute(RS_BOARD_FORM_FOCUS_ATTR);
    };
  }, [columnRef, syncKey, enableScrollFormFocus]);
}
